"use client";

import { cn } from "@/lib/cn";

/**
 * A horizontal run of steps: a dot per step on a connecting rail, filled up to
 * the current one.
 *
 * The dots and the rail are drawn with `::before`/`::after` so the labels stay
 * plain text — that's what lets a long label wrap without pushing its dot off the
 * rail.
 *
 * The grid is an inline `gridTemplateColumns` rather than `grid-cols-${n}`:
 * Tailwind can't see an interpolated class name, so the legacy version emitted
 * `grid-cols-4` and got nothing at all — every step stacked into one column.
 */
export function Stepper({
  steps,
  currentStepId,
  onStepChange,
  className,
}: {
  steps: { id: string; label: string }[];
  currentStepId: string | null;
  onStepChange: (id: string) => void;
  className?: string;
}) {
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);

  return (
    <div
      className={cn("relative grid grid-rows-1 gap-2", className)}
      style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
    >
      {steps.map((step, index) => {
        const isActive = index === currentIndex;
        const isCompleted = index < currentIndex;

        return (
          <button
            key={step.id}
            type="button"
            aria-current={isActive ? "step" : undefined}
            onClick={() => onStepChange(step.id)}
            className={cn(
              "relative flex flex-col items-center gap-1 pt-10",
              // The rail, drawn leftwards from each dot but the first.
              "[&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:top-4",
              "[&:not(:first-child)]:before:-z-1 [&:not(:first-child)]:before:-ml-[100%]",
              "[&:not(:first-child)]:before:h-[4px] [&:not(:first-child)]:before:w-full",
              "[&:not(:first-child)]:before:-translate-y-1/2",
              // The dot.
              "after:absolute after:left-1/2 after:top-1 after:z-[1] after:h-6 after:w-6",
              "after:-translate-x-1/2 after:rounded-full after:border-4 after:border-white",
              "after:shadow-md after:outline-3 after:outline",
              "focus-visible:outline-none focus-visible:after:ring-4",
              "focus-visible:after:ring-gray-400/50 focus-visible:after:ring-offset-4",
              isActive &&
                "after:bg-primary after:outline-primary [&:not(:first-child)]:before:bg-primary",
              isCompleted &&
                "after:bg-white after:outline-primary hover:after:border-gray-100 hover:after:bg-gray-100 [&:not(:first-child)]:before:bg-primary",
              !isActive &&
                !isCompleted &&
                "after:bg-white after:outline-gray-400 hover:after:border-gray-100 hover:after:bg-gray-100 [&:not(:first-child)]:before:bg-gray-400"
            )}
          >
            <span className="px-2">{step.label}</span>
          </button>
        );
      })}
    </div>
  );
}
